// European Liftgate Fee variant - 8761430114502
// European LTL Freight Fee variant - 8761430212806

(() => {
  // =========================
  // CONFIG (HempWood)
  // =========================

  // --- US MARKET FEES ---
  const US_FREIGHT_ID  = 45441827176646; // $0.50 per unit
  const US_LIFTGATE_ID = 45441841594566; // $99 flat

  // --- EUROPEAN MARKET FEES (RESERVED — UNUSED IN CURRENT FLOW) ---
  // Kept in inventory for future use. When EU stock is available,
  // re-introduce an EU region branch in the region detection below
  // that mirrors the US logic and points at these IDs.
  const EU_FREIGHT_ID  = 45466469695686; // Euro Freight (reserved)
  const EU_LIFTGATE_ID = 45466467991750; // Euro Liftgate (reserved)

  const QUOTE_REQUIRED_PRODUCT_TYPES = new Set([
    'Table and desk top', 'Table Tops', 'Lumber', 'Panel', 'Plywood',
    'desk top', 'desk tops'
  ]);

  const FLOORING_PRODUCT_TYPES = new Set([
    'Flooring', 'Flooring & Carpet',
  ]);

  // =========================
  // Variant ID Health Check
  // =========================
  (async () => {
    const feeVariants = [
      { id: US_FREIGHT_ID,  label: 'US Freight Fee' },
      { id: US_LIFTGATE_ID, label: 'US Liftgate Fee' },
      { id: EU_FREIGHT_ID,  label: 'EU Freight Fee (reserved)' },
      { id: EU_LIFTGATE_ID, label: 'EU Liftgate Fee (reserved)' },
    ];
    for (const { id, label } of feeVariants) {
      try {
        const r = await fetch(`/variants/${id}.js`);
        if (!r.ok) {
          console.warn(`[HempWood Shipping] Fee variant "${label}" (ID: ${id}) returned ${r.status}. Update the ID in hempwood-shipping.js immediately.`);
        }
      } catch (e) {
        console.warn(`[HempWood Shipping] Could not verify fee variant "${label}" (ID: ${id}):`, e.message);
      }
    }
  })();

  // Minimum order threshold
  const MIN_FLOORING_SQFT = 15;
  const SQFT_TO_M2 = 0.092903;
  const M2_TO_SQFT = 10.7639;

  const CART_ATTR_LTL_TYPE = 'LTL Type';
  const CART_ATTR_QUOTE_REQUIRED = 'Shipping Quote Required';
  const CART_ATTR_DELIVERY_METHOD = 'Delivery Method';

  // =========================
  // State
  // =========================
  let syncing = false;
  let pending = false;
  let fetchPatched = false;
  let observerAttached = false;
  let forceSyncUntil = 0;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
  const rootPath = root.endsWith('/') ? root : (root + '/');

  // =========================
  // Market Logic (Option A: US vs ROW only)
  // =========================
  // Distributor countries (DE, CH, AT, NL, ES, BG, SE, DK, GB) are
  // handled separately by the geo-banner in theme.liquid which
  // redirects them to /pages/international-distributors. If they
  // ignore that and reach the cart, they fall through to ROW which
  // produces the quote-required message — that's an acceptable
  // fallback.
  const MARKET_COUNTRY = (window.HW && window.HW.marketCountry)
    ? String(window.HW.marketCountry).trim().toUpperCase()
    : 'US';

  const region = (MARKET_COUNTRY === 'US') ? 'US' : 'ROW';

  // Only US has active fee variants in the current flow.
  const CURRENT_FREIGHT_ID  = (region === 'US') ? US_FREIGHT_ID  : null;
  const CURRENT_LIFTGATE_ID = (region === 'US') ? US_LIFTGATE_ID : null;

  // =========================
  // Helpers
  // =========================
  function $(sel, scope = document) { return scope.querySelector(sel); }
  function $all(sel, scope = document) { return Array.from(scope.querySelectorAll(sel)); }

  function hasShippingUI() {
    return Boolean(document.querySelector('.hempwood-shipping-ui'));
  }

  function markForceSync(ms = 1200) {
    forceSyncUntil = Date.now() + ms;
  }

  function shouldSyncNow() {
    if (Date.now() < forceSyncUntil) return true;
    return window.location.pathname.includes('/cart') || hasShippingUI();
  }

  async function getCart() {
    const r = await fetch(rootPath + 'cart.js', { credentials: 'same-origin' });
    return await r.json();
  }

  async function updateAttributes(attrs) {
    await fetch(rootPath + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attrs })
    });
  }

  async function addVariant(id, quantity) {
    try {
      const response = await fetch(rootPath + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id, quantity }] })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`HTTP ${response.status}: ${errorData.description || response.statusText}`);
      }

    } catch (e) {
      console.warn(`[HempWood Shipping] Fee auto-add safely aborted for variant ${id}. Reason:`, e.message);
    }
  }

  async function changeLine(line, quantity) {
    await fetch(rootPath + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line, quantity })
    });
  }

  function isInternalFeeItem(item) {
    return [US_FREIGHT_ID, US_LIFTGATE_ID, EU_FREIGHT_ID, EU_LIFTGATE_ID].includes(item.id);
  }

  function norm(s) { return String(s || '').trim().toLowerCase(); }

  function isSampleItem(i) {
    if (isInternalFeeItem(i)) return false;
    return norm(i.product_title).includes('sample');
  }

  // True if the cart contains ONLY sample products (and no real items).
  // Sample-only carts ship free worldwide and skip the quote-required UI.
  function isSampleOnlyCart(cart) {
    const realItems = cart.items.filter(i => !isInternalFeeItem(i));
    if (realItems.length === 0) return false;
    return realItems.every(isSampleItem);
  }

  function isFlooringItem(i) {
    if (isInternalFeeItem(i)) return false;
    const pt = norm(i.product_type);
    const allowed = new Set(Array.from(FLOORING_PRODUCT_TYPES).map(norm));
    if (allowed.has(pt)) return true;
    const title = norm(i.product_title);
    if (title.includes('flooring') && !title.includes('sample')) return true;
    return false;
  }

  function computeFlooring(cart) {
    let totalSqFt = 0;
    let totalSqM = 0;

    cart.items.filter(isFlooringItem).forEach(i => {
      const qty = Number(i.quantity) || 0;
      const title = norm(i.product_title);

      if (title.includes('bio')) {
        totalSqM += qty;
        totalSqFt += (qty * M2_TO_SQFT);
      } else {
        totalSqFt += qty;
        totalSqM += (qty * SQFT_TO_M2);
      }
    });

    return { totalSqFt, totalSqM };
  }

  function hasQuoteRequiredTypes(cart) {
    const allowed = new Set(Array.from(QUOTE_REQUIRED_PRODUCT_TYPES).map(norm));
    return cart.items.some(i => !isInternalFeeItem(i) && allowed.has(norm(i.product_type)));
  }

  function lineIndexByVariantId(cart, variantId) {
    const idx = cart.items.findIndex(i => i.id === variantId);
    return idx === -1 ? null : idx + 1;
  }

  function itemByVariantId(cart, variantId) {
    return cart.items.find(i => i.id === variantId) || null;
  }

  function getUiRoots() {
    return $all('.hempwood-shipping-ui');
  }

  function selectedLtlTypeFromDom(uiRoot) {
    const checked = $('input[name="ltl_type"]:checked', uiRoot);
    return checked ? checked.value : null;
  }

  function applyChoiceToDom(uiRoot, choice) {
    if (!choice) return;
    const input = $(`input[name="ltl_type"][value="${choice}"]`, uiRoot);
    if (input && !input.checked) input.checked = true;
  }

  function show(el, on) {
    if (!el) return;
    el.style.display = on ? '' : 'none';
  }

  function ensureMinOrderWarningEl(uiRoot) {
    let el = $('.min-order-warning', uiRoot);
    if (!el) {
      el = document.createElement('div');
      el.className = 'min-order-warning';
      el.setAttribute('role', 'alert');
      el.style.marginTop = '10px';
      el.style.padding = '10px 12px';
      el.style.border = '1px solid rgba(0,0,0,0.12)';
      el.style.borderRadius = '8px';
      el.style.fontSize = '0.95em';
      el.style.lineHeight = '1.35';
      uiRoot.appendChild(el);
    }
    return el;
  }
  function getCheckoutScopes() {
    const scopes = new Set();
    getUiRoots().forEach(ui => {
      const scope = ui.closest('.cart-footer') || ui.closest('.drawer__footer') || ui.closest('#CartDrawer');
      if (scope) scopes.add(scope);
    });
    if (window.location.pathname.includes('/cart')) scopes.add(document);
    return Array.from(scopes);
  }

  function setCheckoutEnabled(enabled) {
    const scopes = getCheckoutScopes();
    if (scopes.length === 0) return;
    scopes.forEach(scope => {
      const termsCheckbox = scope.querySelector('#cart-terms-and-conditions') || scope.querySelector('input[name="cart_terms"]') || scope.querySelector('input[name="terms"]');
      const termsOk = !termsCheckbox || termsCheckbox.checked;
      const finalEnabled = Boolean(enabled && termsOk);
      scope.querySelectorAll('button[name="checkout"], button[type="submit"][name="checkout"], input[type="submit"][name="checkout"]').forEach(btn => {
          btn.disabled = !finalEnabled;
          btn.setAttribute('aria-disabled', String(!finalEnabled));
      });
      blockDynamicCheckout(scope, !finalEnabled);
    });
  }

  function blockDynamicCheckout(scope, blocked) {
    const containers = scope.querySelectorAll('.additional-checkout-buttons, .additional-checkout-buttons__buttons, .shopify-payment-button, [data-shopify-buttoncontainer], .dynamic-checkout, .shopify-cleanslate');
    containers.forEach(c => {
      const style = window.getComputedStyle(c);
      if (style.position === 'static') c.style.position = 'relative';
      let overlay = c.querySelector('.hw-checkout-blocker');
      if (blocked) {
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'hw-checkout-blocker';
          overlay.setAttribute('aria-hidden', 'true');
          overlay.style.position = 'absolute';
          overlay.style.inset = '0';
          overlay.style.zIndex = '9999';
          overlay.style.background = 'rgba(255,255,255,0.01)';
          overlay.style.pointerEvents = 'auto';
          c.appendChild(overlay);
        }
      } else {
        if (overlay) overlay.remove();
      }
    });
  }

  async function removeAllFees(cart) {
    const fees = [US_FREIGHT_ID, US_LIFTGATE_ID, EU_FREIGHT_ID, EU_LIFTGATE_ID];
    for (const id of fees) {
        if (itemByVariantId(cart, id)) {
            const line = lineIndexByVariantId(cart, id);
            if (line != null) await changeLine(line, 0);
        }
    }
  }

  async function removeWrongMarketFees(cart) {
    // US keeps US fees; everyone else strips all fee variants.
    let feesToRemove = [];
    if (region === 'US') {
        feesToRemove = [EU_FREIGHT_ID, EU_LIFTGATE_ID];
    } else {
        feesToRemove = [US_FREIGHT_ID, US_LIFTGATE_ID, EU_FREIGHT_ID, EU_LIFTGATE_ID];
    }

    for (const id of feesToRemove) {
        if (itemByVariantId(cart, id)) {
            const line = lineIndexByVariantId(cart, id);
            if (line != null) await changeLine(line, 0);
        }
    }
  }

  async function clearLtlAttributeIfPresent(cart) {
    const cartAttr = cart.attributes ? cart.attributes[CART_ATTR_LTL_TYPE] : null;
    if (cartAttr) await updateAttributes({ [CART_ATTR_LTL_TYPE]: '' });
  }

  async function setQuoteAttribute(cart, yes) {
    const current = cart.attributes ? cart.attributes[CART_ATTR_QUOTE_REQUIRED] : null;
    if (yes) {
      if (current !== 'Yes') await updateAttributes({ [CART_ATTR_QUOTE_REQUIRED]: 'Yes' });
    } else {
      if (current) await updateAttributes({ [CART_ATTR_QUOTE_REQUIRED]: '' });
    }
  }

  async function sync() {
    if (!shouldSyncNow()) return;

    if (syncing) { pending = true; return; }
    syncing = true;

    try {
      const cart = await getCart();
      const uiRoots = getUiRoots();

      await removeWrongMarketFees(cart);

      const realItems = cart.items.filter(i => !isInternalFeeItem(i));
      if (realItems.length === 0) {
        uiRoots.forEach(rootEl => {
          show($('.quote-warning', rootEl), false);
          show($('.ltl-type', rootEl), false);
          show($('.ltl-warning', rootEl), false);
          show($('.min-order-warning', rootEl), false);
        });
        setCheckoutEnabled(true);
        await removeAllFees(cart);
        await clearLtlAttributeIfPresent(cart);
        await setQuoteAttribute(cart, false);
        return;
      }

      // -------------------------------------------------------------
      // ROW BRANCH: everyone outside the US
      // -------------------------------------------------------------
      // Sample-only carts ship free worldwide — skip the quote UI.
      // All other ROW carts show the quote message, get tagged for
      // the team to follow up, and proceed to checkout with $0
      // shipping (Philosophy B: place order, quote separately).
      if (region === 'ROW') {
        const sampleOnly = isSampleOnlyCart(cart);

        uiRoots.forEach(rootEl => {
          show($('.quote-warning', rootEl), !sampleOnly);
          show($('.ltl-type', rootEl), false);
          show($('.ltl-warning', rootEl), false);
          show($('.min-order-warning', rootEl), false);
        });
        setCheckoutEnabled(true);
        await setQuoteAttribute(cart, !sampleOnly);
        await removeAllFees(cart);
        await clearLtlAttributeIfPresent(cart);
        return;
      }

      // -------------------------------------------------------------
      // US BRANCH (existing US flow)
      // -------------------------------------------------------------
      const flooringStats = computeFlooring(cart);
      const sqft = flooringStats.totalSqFt;
      const sqm = flooringStats.totalSqM;

      const hasFlooring = sqft > 0;
      const hasQuoteItems = hasQuoteRequiredTypes(cart);

      if (hasQuoteItems && !hasFlooring) {
        uiRoots.forEach(rootEl => {
          show($('.quote-warning', rootEl), true);
          show($('.ltl-type', rootEl), false);
          show($('.ltl-warning', rootEl), false);
          show($('.min-order-warning', rootEl), false);
        });
        setCheckoutEnabled(true);
        await setQuoteAttribute(cart, true);
        await removeAllFees(cart);
        await clearLtlAttributeIfPresent(cart);
        return;
      }

      const isMixed = hasFlooring && hasQuoteItems;
      uiRoots.forEach(rootEl => show($('.quote-warning', rootEl), isMixed));
      await setQuoteAttribute(cart, isMixed);

      if (hasFlooring) {
        const belowMinimum = sqft < MIN_FLOORING_SQFT;
        uiRoots.forEach(rootEl => {
          const el = ensureMinOrderWarningEl(rootEl);
          if (belowMinimum) {
            const missingSqFt = Math.ceil(MIN_FLOORING_SQFT - sqft);
            const m2 = (MIN_FLOORING_SQFT * SQFT_TO_M2).toFixed(1);
            el.textContent = `Minimum flooring order is ${MIN_FLOORING_SQFT} sq ft (≈ ${m2} m²). Add approx ${missingSqFt} more sq ft to continue.`;
          }
          show(el, belowMinimum);
        });

        let deliveryMethod = 'ship';
        for (const rootEl of uiRoots) {
          const checked = $('input[name="delivery_method"]:checked', rootEl);
          if (checked) {
            deliveryMethod = checked.value;
            break;
          }
        }

        if (deliveryMethod === 'pickup') {
          uiRoots.forEach(rootEl => {
            show($('.ltl-type', rootEl), false);
            show($('.ltl-warning', rootEl), false);
          });

          setCheckoutEnabled(!belowMinimum);
          await removeAllFees(cart);
          await clearLtlAttributeIfPresent(cart);
          await updateAttributes({ [CART_ATTR_DELIVERY_METHOD]: 'Local Pickup' });

        } else {

          const isParcelShipment = sqft < 100;

          if (isParcelShipment) {
            uiRoots.forEach(rootEl => {
              show($('.ltl-type', rootEl), false);
              show($('.ltl-warning', rootEl), false);
            });

            setCheckoutEnabled(!belowMinimum);
            await clearLtlAttributeIfPresent(cart);
            await updateAttributes({ [CART_ATTR_DELIVERY_METHOD]: 'Ship to Me (Standard)' });

            const freightChargeQty = 98;
            const freightItem = itemByVariantId(cart, CURRENT_FREIGHT_ID);
            if (!freightItem) {
              await addVariant(CURRENT_FREIGHT_ID, freightChargeQty);
            } else if ((Number(freightItem.quantity) || 0) !== freightChargeQty) {
              const line = lineIndexByVariantId(cart, CURRENT_FREIGHT_ID);
              if (line != null) await changeLine(line, freightChargeQty);
            }

            const liftItem = itemByVariantId(cart, CURRENT_LIFTGATE_ID);
            if (liftItem) {
              const line = lineIndexByVariantId(cart, CURRENT_LIFTGATE_ID);
              if (line != null) await changeLine(line, 0);
            }

          } else {
            uiRoots.forEach(rootEl => show($('.ltl-type', rootEl), true));
            await updateAttributes({ [CART_ATTR_DELIVERY_METHOD]: 'Ship to Me' });

            const cartAttr = cart.attributes ? cart.attributes[CART_ATTR_LTL_TYPE] : null;
            let domChoice = null;
            for (const rootEl of uiRoots) {
              domChoice = selectedLtlTypeFromDom(rootEl);
              if (domChoice) break;
            }
            const choice = domChoice || cartAttr;

            if (!domChoice && cartAttr) {
              uiRoots.forEach(rootEl => applyChoiceToDom(rootEl, cartAttr));
            }

            const hasChoice = (choice === 'commercial_no_liftgate' || choice === 'commercial_liftgate' || choice === 'residential');

            uiRoots.forEach(rootEl => show($('.ltl-warning', rootEl), (!hasChoice && !belowMinimum)));
            setCheckoutEnabled(hasChoice && !belowMinimum);

            if (domChoice && domChoice !== cartAttr) {
              await updateAttributes({ [CART_ATTR_LTL_TYPE]: domChoice });
            }

            // US LTL is charged by SqFt. API requires integers (Math.ceil).
            const freightChargeQty = Math.ceil(sqft);

            const freightItem = itemByVariantId(cart, CURRENT_FREIGHT_ID);
            if (!freightItem) {
              await addVariant(CURRENT_FREIGHT_ID, freightChargeQty);
            } else if ((Number(freightItem.quantity) || 0) !== freightChargeQty) {
              const line = lineIndexByVariantId(cart, CURRENT_FREIGHT_ID);
              if (line != null) await changeLine(line, freightChargeQty);
            }

            const wantLiftgate = (choice === 'residential' || choice === 'commercial_liftgate') ? 1 : 0;
            const liftItem = itemByVariantId(cart, CURRENT_LIFTGATE_ID);

            if (wantLiftgate) {
              if (!liftItem) {
                await addVariant(CURRENT_LIFTGATE_ID, 1);
              } else if ((Number(liftItem.quantity) || 0) !== 1) {
                const line = lineIndexByVariantId(cart, CURRENT_LIFTGATE_ID);
                if (line != null) await changeLine(line, 1);
              }
            } else {
              if (liftItem) {
                const line = lineIndexByVariantId(cart, CURRENT_LIFTGATE_ID);
                if (line != null) await changeLine(line, 0);
              }
            }
          }
        }

      } else {
        uiRoots.forEach(rootEl => {
          show($('.ltl-type', rootEl), false);
          show($('.ltl-warning', rootEl), false);
          show($('.quote-warning', rootEl), false);
          show($('.min-order-warning', rootEl), false);
        });
        setCheckoutEnabled(true);
        await removeAllFees(cart);
        await clearLtlAttributeIfPresent(cart);
        await setQuoteAttribute(cart, false);
      }
    } catch (e) {
      console.error('[HempWood Shipping] sync failed:', e);
      setCheckoutEnabled(true);
    } finally {
      syncing = false;
      if (pending) { pending = false; setTimeout(sync, 75); }
    }
  }

  function bindUI() {
    document.addEventListener('change', (e) => {
      if (e.target && e.target.matches('input[name="delivery_method"]')) sync();
      if (e.target && e.target.matches('input[name="ltl_type"]')) sync();
      if (e.target && (e.target.matches('#cart-terms-and-conditions') || e.target.matches('input[name="terms"]'))) sync();
    });
  }

  function patchFetchForAjaxCart() {
    if (fetchPatched) return;
    fetchPatched = true;
    const original = window.fetch;
    window.fetch = async (...args) => {
      const res = await original(...args);
      try {
        const url = String(args[0] || '');
        if (url.includes('/cart/') || url.includes('cart.js') || url.includes('section_id=cart-drawer')) {
          markForceSync();
          if (!syncing) setTimeout(sync, 100);
        }
      } catch (_) {}
      return res;
    };
  }

  function observeForDrawerRerenders() {
    if (observerAttached) return;
    observerAttached = true;
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes || []) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.('.hempwood-shipping-ui') || node.querySelector?.('.hempwood-shipping-ui')) {
            markForceSync();
            setTimeout(sync, 50);
            return;
          }
        }
      }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindUI();
    patchFetchForAjaxCart();
    observeForDrawerRerenders();
    sync();
  });
})();